import { useLayoutEffect, useRef } from 'react';
import { getScrollLeftToReveal, ScrollRange } from '../utils';

type ScrollTo = (coord: { x: number; y: number }) => void;
type RevealTarget = { key: string; range: ScrollRange; viewportWidth: number };
type TableScrollHandlers = {
  handleScroll: (scrollX: number, scrollY: number) => void;
  handleDataUpdated: (nextData: object[], scrollTo: ScrollTo) => void;
};

// rsuite resets the scroll to the top left on every data update and the horizontal scroll whenever the number of columns
// changes (e.g. expanding a cluster). Remember the position and put it back once the table has settled its new content.
// A new page of rows (pageKey) starts at the top. When a new reveal target shows up (a cluster was just expanded),
// scroll just enough to bring its range into view.
export const useKeepTableScrollPosition = (tableKey: unknown, pageKey: string, revealTarget: RevealTarget | null): TableScrollHandlers => {
  const scrollLeft = useRef(0);
  const scrollTop = useRef(0);
  const restoredPageKey = useRef(pageKey);
  const revealedKey = useRef<string | null>(revealTarget?.key ?? null);

  useLayoutEffect(() => {
    scrollLeft.current = 0;
    scrollTop.current = 0;
    revealedKey.current = revealTarget?.key ?? null;
    // Only a new table instance (new width) starts over; a reveal target change is handled on data update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableKey]);

  const handleScroll = (scrollX: number, scrollY: number): void => {
    scrollLeft.current = Math.abs(scrollX);
    scrollTop.current = Math.abs(scrollY);
  };

  const handleDataUpdated = (_nextData: object[], scrollTo: ScrollTo): void => {
    const pendingReveal = revealTarget && revealTarget.key !== revealedKey.current ? revealTarget : null;
    revealedKey.current = revealTarget?.key ?? null;
    const nextScrollLeft = pendingReveal ? getScrollLeftToReveal(scrollLeft.current, pendingReveal.range, pendingReveal.viewportWidth) : scrollLeft.current;
    const nextScrollTop = restoredPageKey.current === pageKey ? scrollTop.current : 0;
    restoredPageKey.current = pageKey;
    scrollLeft.current = nextScrollLeft;
    scrollTop.current = nextScrollTop;
    if (nextScrollLeft <= 0 && nextScrollTop <= 0) return;

    // The Table recalculates its content size right after this callback; the microtask runs once that update is flushed.
    queueMicrotask(() => scrollTo({ x: nextScrollLeft, y: nextScrollTop }));
  };

  return { handleScroll, handleDataUpdated };
};
